import { useParams } from "react-router";
import { format } from "date-fns";
import { DownloadIcon, MoreVerticalIcon, ReloadIcon } from "vega-icons";

import { useGetDealTrackingForGp } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { TrackingNdaStatus } from "@vega-api/generated/types/trackingNdaStatus";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { DealTrackingAnalyticsCards } from "../components/DealTrackingMetrics/DealTrackingAnalyticsCards";
import { PieChartMetrics } from "../components/PieChartMetrics/PieChartMetrics";
import {
  SubtotalsByStage,
  subtotalsByStageToStages,
} from "../components/SubtotalsByStage/SubtotalsByStage";
import { useTrackingExportColumns } from "../components/TrackingTable/hooks/useTrackingExportColumns";
import { TrackingTable } from "../components/TrackingTable/TrackingTable";
import { useDealCurrency } from "../hooks/useDealCurrency";
import { useDealName } from "../hooks/useDealName";
import { useExportFilename } from "../hooks/useExportFilename";
import { useTableExport } from "../hooks/useTableExport";
import { splitDealTrackingMetrics } from "../utils/metricGuards";

export const CoInvestmentTrackingPage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const { id: dealId = "" } = useParams<{ id: string }>();

  const { currency: dealCurrency } = useDealCurrency(dealId);
  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const {
    data: trackingResponse,
    dataUpdatedAt: trackingDataUpdatedAt,
    isPending: isTrackingDataPending,
    isFetching: isTrackingDataFetching,
    refetch: refetchTrackingData,
  } = useGetDealTrackingForGp(dealId, {
    query: {
      enabled: !!dealId,
      select: (data) => {
        const { data: trackingData } = data;

        if (!trackingData) {
          return undefined;
        }

        const metrics = trackingData.overview.metrics.map((metric) => ({
          ...metric,
          label: metric.label,
        }));

        const { topMetrics, pieChartMetrics } =
          splitDealTrackingMetrics(metrics);

        const baseExportData = trackingData.limitedPartnerBreakdown;
        const totals = trackingData.totals;
        let exportData = baseExportData;

        if (totals) {
          const totalRow: DealTrackingLimitedPartnerBreakdown = {
            dealShortlistItemId: "",
            dealOpportunityId: "",
            limitedPartnerId: "",
            limitedPartnerName: m.common_total(),
            tier: "—",
            numberOfNdasSigned: 0,
            numberOfNdasIssued: 0,
            ndaStatus: TrackingNdaStatus.NOT_APPLICABLE,
            isRejected: false,
            ticketSize: totals.ticketSize,
            indicativeAmount: totals.indicativeAmount,
            allocationAmount: totals.allocationAmount,
            gpIndicativeOrder: totals.gpIndicativeOrder,
            estimatedTicketSizeMin: totals.estimatedTicketSizeMin,
            estimatedTicketSizeMax: totals.estimatedTicketSizeMax,
            commitmentAmount: totals.commitmentAmount,
          };
          exportData = [totalRow, ...baseExportData];
        }

        return {
          ...trackingData,
          overview: {
            ...trackingData.overview,
            topMetrics,
            pieChartMetrics,
          },
          exportData,
          stagesForSubtotals: trackingData.subtotalsByStage?.length
            ? subtotalsByStageToStages(trackingData.subtotalsByStage)
            : [],
        };
      },
    },
  });
  const topMetrics = trackingResponse?.overview.topMetrics || [];
  const pieChartMetrics = trackingResponse?.overview.pieChartMetrics || [];

  const exportData = trackingResponse?.exportData || [];

  const lastUpdated = trackingDataUpdatedAt
    ? format(new Date(trackingDataUpdatedAt), "PPpp")
    : null;

  const { columns: exportColumns, groupedHeaders: exportGroupedHeaders } =
    useTrackingExportColumns();
  const exportFilename = useExportFilename(dealId, "tracking");

  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: exportData,
    columns: exportColumns,
    filename: exportFilename,
    groupedHeaders: exportGroupedHeaders,
  });

  return (
    <div className="mt-1">
      <CoInvestmentPageHeader
        action={
          <div className="flex items-center gap-2">
            {lastUpdated ? (
              <p
                className="body-small-regular text-text/secondary"
                data-testid="tracking-last-updated"
              >
                {m.coInvestments_tracking_lastUpdated({
                  timestamp: lastUpdated,
                })}
              </p>
            ) : null}
            <ThemedButton
              dataTestId="tracking-refresh-button"
              disabled={isTrackingDataFetching}
              icon={
                <ReloadIcon
                  className={
                    isTrackingDataFetching ? "animate-spin" : undefined
                  }
                />
              }
              onClick={refetchTrackingData}
              variant="secondary"
            >
              {m.btn_refresh()}
            </ThemedButton>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ThemedButton
                  ariaLabel="Export actions"
                  className="h-10 w-10"
                  dataTestId="tracking-actions-menu"
                  disabled={isTrackingDataPending || isExporting}
                  icon={<MoreVerticalIcon />}
                  variant="secondary"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2">
                <DropdownMenuItem onSelect={exportToCsv}>
                  <DownloadIcon className="w-4 h-4" />
                  <p className="body-base-medium">{m.btn_exportAsCsv()}</p>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={exportToExcel}>
                  <DownloadIcon className="w-4 h-4" />
                  <p className="body-base-medium">{m.btn_exportAsExcel()}</p>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        fundName={dealName}
        isLoading={isDealNameLoading}
        title={m.coInvestments_nav_tracking()}
      />

      <div className="mt-6 space-y-6">
        {/* Metrics Section */}
        <DealTrackingAnalyticsCards
          isLoading={isTrackingDataPending}
          metrics={topMetrics}
        />
        <SubtotalsByStage
          isLoading={isTrackingDataPending}
          stages={trackingResponse?.stagesForSubtotals}
        />
        <PieChartMetrics
          centerLabel={m.coInvestments_tracking_totalCount()}
          dataTestId="deal-tracking-pie-charts"
          isLoading={isTrackingDataPending}
          pieChartMetrics={pieChartMetrics}
        />

        <div className="space-y-4 pb-52">
          <TrackingTable
            data={trackingResponse?.limitedPartnerBreakdown || []}
            dealCurrency={dealCurrency}
            dealId={dealId}
            isLoading={isTrackingDataPending}
            totals={trackingResponse?.totals}
          />
        </div>
      </div>
    </div>
  );
};
